import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import { lazyNamed } from "src/utils/lazy-named";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { NATIVE_DELIVERY_PERMISSIONS } from "src/cep/permissions";

export const ASSETS_PATH = "/native-delivery/assets";
export const CONDITION_TEMPLATES_PATH = "/native-delivery/assets/conditions";
const CONTENT_BLOCKS_PATH = "/native-delivery/assets/content-blocks";
const EMAIL_TEMPLATES_PATH = "/native-delivery/assets/email-templates";
const WHATSAPP_TEMPLATES_PATH = "/native-delivery/assets/whatsapp-templates";

const ContentBlockDetail = lazyNamed(
  () => import("./content-blocks/content-block-detail"),
  "ContentBlockDetail",
);
const NewContentBlockDraft = lazyNamed(
  () => import("./content-blocks/new-content-block-draft"),
  "NewContentBlockDraft",
);
const ContentBlocksList = lazyNamed(
  () => import("./content-blocks/content-blocks-list"),
  "ContentBlocksList",
);
const EmailTemplateComposer = lazyNamed(
  () => import("./email-templates/email-template-composer"),
  "EmailTemplateComposer",
);
const NewEmailTemplateDraft = lazyNamed(
  () => import("./email-templates/new-email-template-draft"),
  "NewEmailTemplateDraft",
);
const EmailTemplatesList = lazyNamed(
  () => import("./email-templates/email-templates-list"),
  "EmailTemplatesList",
);
const WhatsAppTemplatesList = lazyNamed(
  () => import("./whatsapp-templates/whatsapp-templates-list"),
  "WhatsAppTemplatesList",
);
const WhatsAppTemplateDetail = lazyNamed(
  () => import("./whatsapp-templates/whatsapp-template-detail"),
  "WhatsAppTemplateDetail",
);
const ConditionsList = lazyNamed(
  () => import("./conditions/conditions-list"),
  "ConditionsList",
);
const ConditionTemplateDetail = lazyNamed(
  () => import("./conditions/condition-template-detail"),
  "ConditionTemplateDetail",
);

export const routes: RouteObject[] = [
  {
    path: "native-delivery/assets/conditions/:conditionId",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <FeatureFlagRoute
        flagKey="cepDynamicContentEnabled"
        redirect={ASSETS_PATH}
      >
        <PermissionedRoute
          permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
          redirect={CONDITION_TEMPLATES_PATH}
        >
          <ConditionTemplateDetail />
        </PermissionedRoute>
      </FeatureFlagRoute>
    ),
  },
  {
    path: "native-delivery/assets/content-blocks/new",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
        redirect={CONTENT_BLOCKS_PATH}
      >
        <NewContentBlockDraft />
      </PermissionedRoute>
    ),
  },
  {
    path: "native-delivery/assets/content-blocks/:blockId",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
        redirect={CONTENT_BLOCKS_PATH}
      >
        <ContentBlockDetail />
      </PermissionedRoute>
    ),
  },
  {
    // The tab is read on mount only, so switching tabs leaves this URL alone.
    path: "native-delivery/assets/content-blocks/:blockId/localize",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
        redirect={CONTENT_BLOCKS_PATH}
      >
        <ContentBlockDetail initialTab="localize" />
      </PermissionedRoute>
    ),
  },
  {
    path: "native-delivery/assets/whatsapp-templates/:templateId",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <FeatureFlagRoute flagKey="cepWhatsappChannel" redirect={ASSETS_PATH}>
        <PermissionedRoute
          permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
          redirect={WHATSAPP_TEMPLATES_PATH}
        >
          <WhatsAppTemplateDetail />
        </PermissionedRoute>
      </FeatureFlagRoute>
    ),
  },
  {
    path: "native-delivery/assets/email-templates/new",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
        redirect={EMAIL_TEMPLATES_PATH}
      >
        <NewEmailTemplateDraft />
      </PermissionedRoute>
    ),
  },
  {
    path: "native-delivery/assets/email-templates/:templateId",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={NATIVE_DELIVERY_PERMISSIONS.manageAssets}
        redirect={EMAIL_TEMPLATES_PATH}
      >
        <EmailTemplateComposer />
      </PermissionedRoute>
    ),
  },
  {
    path: "native-delivery/assets",
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        // Matches the leftmost tab in AssetsPage.
        element: <Navigate to="email-templates" replace />,
      },
      {
        path: "content-blocks",
        spacesAccess: "hidden-from-limited-view",
        element: <ContentBlocksList />,
      },
      {
        path: "email-templates",
        spacesAccess: "hidden-from-limited-view",
        element: <EmailTemplatesList />,
      },
      {
        path: "whatsapp-templates",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute flagKey="cepWhatsappChannel" redirect={ASSETS_PATH}>
            <WhatsAppTemplatesList />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "conditions",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="cepDynamicContentEnabled"
            redirect={ASSETS_PATH}
          >
            <ConditionsList />
          </FeatureFlagRoute>
        ),
      },
    ],
  },
];
